import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { TComponentType } from '../AppPage2/types'

const STORAGE_SCHEMA_VERSION = 1
const MAX_SELECTED_CONTEXT_ITEMS = 30
const MAX_RESPONSE_STRING_SIZE = 100_000

export type AiChatComponentContextRecord = {
  selectionKey: string
  componentId: string
  componentType: TComponentType
  containerId?: string
  appKey?: string
  projectId?: number
  organisationId?: number
  page?: string
  pageId?: string
  queryKey: unknown[]
  filters: Record<string, unknown>[]
  response: unknown
  dataUpdatedAt: number
  capturedAt: string
}

type StoredContextState = {
  schemaVersion: number
  updatedAt: string
  selected: Record<string, AiChatComponentContextRecord>
}

type AiChatComponentContextValue = {
  isSelectingContext: boolean
  selectedByKey: Record<string, AiChatComponentContextRecord>
  selectedContext: AiChatComponentContextRecord[]
  selectedCount: number
  storageKey: string
  startSelection: () => void
  finishSelection: () => void
  addOrUpdateSelection: (record: Omit<AiChatComponentContextRecord, 'selectionKey' | 'capturedAt'>) => void
  removeSelection: (selectionKey: string) => void
  clearSelection: () => void
  hasSelection: (selectionKey: string) => boolean
}

const AiChatComponentContext = createContext<AiChatComponentContextValue | undefined>(undefined)

function createStorageKey(organisationId?: number, projectId?: number, appKey?: string) {
  return `ai-chat-component-context:v1:${organisationId ?? -1}:${projectId ?? -1}:${appKey ?? 'unknown'}`
}

function createSelectionKey({
  pageId,
  componentType,
  componentId,
}: {
  pageId?: string
  componentType: TComponentType
  componentId: string
}) {
  return `${pageId ?? 'unknown-page'}::${componentType}::${componentId}`
}

function emptyState(): StoredContextState {
  return {
    schemaVersion: STORAGE_SCHEMA_VERSION,
    updatedAt: new Date(0).toISOString(),
    selected: {},
  }
}

function readState(storageKey: string): StoredContextState {
  if (typeof window === 'undefined') return emptyState()
  const raw = window.sessionStorage.getItem(storageKey)
  if (!raw) return emptyState()
  try {
    const parsed = JSON.parse(raw) as StoredContextState
    if (parsed.schemaVersion !== STORAGE_SCHEMA_VERSION) return emptyState()
    return {
      schemaVersion: STORAGE_SCHEMA_VERSION,
      updatedAt: parsed.updatedAt || new Date().toISOString(),
      selected: parsed.selected || {},
    }
  } catch {
    return emptyState()
  }
}

function writeState(storageKey: string, state: StoredContextState) {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(storageKey, JSON.stringify(state))
  window.dispatchEvent(
    new StorageEvent('storage', {
      key: storageKey,
      newValue: JSON.stringify(state),
      storageArea: sessionStorage,
    })
  )
}

function sanitizeResponseForStorage(response: unknown): unknown {
  try {
    const serialized = JSON.stringify(response)
    if (serialized.length <= MAX_RESPONSE_STRING_SIZE) {
      return JSON.parse(serialized) as unknown
    }
    return {
      _truncated: true,
      _reason: `Response exceeded ${MAX_RESPONSE_STRING_SIZE} characters`,
      _preview: serialized.slice(0, MAX_RESPONSE_STRING_SIZE),
    }
  } catch {
    return {
      _truncated: true,
      _reason: 'Response could not be serialized',
    }
  }
}

export function AiChatComponentContextProvider({ children }: { children: React.ReactNode }) {
  const { appKey, projectId, organisationId } = useAppContext()
  const [isSelectingContext, setIsSelectingContext] = useState(false)
  const storageKey = useMemo(() => createStorageKey(organisationId, projectId, appKey), [organisationId, projectId, appKey])
  const [selectedByKey, setSelectedByKey] = useState<Record<string, AiChatComponentContextRecord>>({})

  useEffect(() => {
    const state = readState(storageKey)
    setSelectedByKey(state.selected)
  }, [storageKey])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea !== sessionStorage) return
      if (event.key !== storageKey) return
      setSelectedByKey(readState(storageKey).selected)
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [storageKey])

  const persistSelected = useCallback(
    (nextSelected: Record<string, AiChatComponentContextRecord>) => {
      setSelectedByKey(nextSelected)
      writeState(storageKey, {
        schemaVersion: STORAGE_SCHEMA_VERSION,
        updatedAt: new Date().toISOString(),
        selected: nextSelected,
      })
    },
    [storageKey]
  )

  const startSelection = useCallback(() => setIsSelectingContext(true), [])
  const finishSelection = useCallback(() => setIsSelectingContext(false), [])

  const addOrUpdateSelection = useCallback(
    (record: Omit<AiChatComponentContextRecord, 'selectionKey' | 'capturedAt'>) => {
      const selectionKey = createSelectionKey({
        pageId: record.pageId,
        componentType: record.componentType,
        componentId: record.componentId,
      })
      const normalizedRecord: AiChatComponentContextRecord = {
        ...record,
        selectionKey,
        capturedAt: new Date().toISOString(),
        response: sanitizeResponseForStorage(record.response),
      }
      const entries = Object.entries(selectedByKey).filter(([key]) => key !== selectionKey)
      entries.unshift([selectionKey, normalizedRecord])
      const capped = entries.slice(0, MAX_SELECTED_CONTEXT_ITEMS)
      persistSelected(Object.fromEntries(capped))
    },
    [persistSelected, selectedByKey]
  )

  const removeSelection = useCallback(
    (selectionKey: string) => {
      const next = { ...selectedByKey }
      delete next[selectionKey]
      persistSelected(next)
    },
    [persistSelected, selectedByKey]
  )

  const clearSelection = useCallback(() => {
    persistSelected({})
  }, [persistSelected])

  const hasSelection = useCallback((selectionKey: string) => Boolean(selectedByKey[selectionKey]), [selectedByKey])

  const selectedContext = useMemo(
    () => Object.values(selectedByKey).sort((a, b) => b.dataUpdatedAt - a.dataUpdatedAt),
    [selectedByKey]
  )

  const value = useMemo(
    () => ({
      isSelectingContext,
      selectedByKey,
      selectedContext,
      selectedCount: selectedContext.length,
      storageKey,
      startSelection,
      finishSelection,
      addOrUpdateSelection,
      removeSelection,
      clearSelection,
      hasSelection,
    }),
    [
      addOrUpdateSelection,
      clearSelection,
      finishSelection,
      hasSelection,
      isSelectingContext,
      removeSelection,
      selectedByKey,
      selectedContext,
      startSelection,
      storageKey,
    ]
  )

  return <AiChatComponentContext.Provider value={value}>{children}</AiChatComponentContext.Provider>
}

export function useAiChatComponentContext() {
  const context = useContext(AiChatComponentContext)
  if (!context) {
    throw new Error('useAiChatComponentContext must be used within AiChatComponentContextProvider')
  }
  return context
}

export function getAiChatComponentSelectionKey({
  pageId,
  componentType,
  componentId,
}: {
  pageId?: string
  componentType: TComponentType
  componentId: string
}) {
  return createSelectionKey({ pageId, componentType, componentId })
}
